import { defineStyle, defineRecipe } from '@chakra-ui/react';

// Card-style selector button (1px border, surface bg, border-active hover)
// that encodes hover / selected / disabled / focus-visible as recipe states
// so consumers get a real <button> with native semantics.
const baseStyleObject = defineStyle({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left',
  gap: '1',
  width: '100%',
  p: '4',
  bg: 'surface.primary',
  border: '1px solid',
  borderColor: 'border.secondary',
  borderRadius: 'lg',
  cursor: 'pointer',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  _hover: {
    borderColor: 'border.active',
    bg: 'surface.tertiary',
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: 'border.action',
    outlineOffset: '2px',
  },
  // Disabled cards must not light up on hover — a native disabled <button>
  // still matches :hover in CSS, so the hover emphasis is explicitly undone.
  _disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    _hover: {
      borderColor: 'border.secondary',
      bg: 'surface.primary',
    },
  },
});

// `card` (default) selected state: emphasise the border + tile fill.
const selectedTrue = defineStyle({
  borderColor: 'border.active',
  bg: 'surface.tertiary',
});

// Flush list-row variant. Strips the card chrome (border, radius, tile
// fill) so selectable rows — the reunification / visitor person pickers —
// get a real <button> + keyboard + aria-pressed via `selected` without the
// bordered-tile look. Selection is a bg highlight (see the row+selected
// compound variant below), not a border.
const rowVariant = defineStyle({
  border: 'none',
  borderRadius: '0',
  bg: 'transparent',
  gap: '0',
  px: '3',
  py: '2',
  _hover: {
    borderColor: 'transparent',
    bg: 'surface.secondary',
  },
  // Keep a disabled row visually inert (no hover fill).
  _disabled: {
    _hover: {
      bg: 'transparent',
    },
  },
});

export const cardButtonRecipe = defineRecipe({
  className: 'aegix-card-button',
  base: baseStyleObject,
  variants: {
    variant: {
      card: {},
      row: rowVariant,
    },
    size: {
      md: {},
      // `lg` bakes in a 44px touch target; the component pairs it with the
      // larger title/description type for touch surfaces (kiosk).
      lg: { minHeight: '11' },
    },
    selected: {
      true: selectedTrue,
      false: {},
    },
  },
  // A selected row highlights via background (the selected-menu-item token),
  // overriding the `card` border-emphasis selection — compounds apply after
  // the individual `variant` / `selected` styles, so this wins for row+selected.
  compoundVariants: [
    {
      variant: 'row',
      selected: true,
      css: {
        bg: 'surface.menuActive',
        _hover: {
          bg: 'surface.menuActive',
        },
      },
    },
  ],
  defaultVariants: {
    variant: 'card',
    size: 'md',
    selected: false,
  },
});
